import { type ReactNode, useEffect } from "react";
import { BoothSearchField } from "../components/BoothSearchField";
import { StatusBlock } from "../components/StatusBlock";
import { type BoothSearchHit } from "./boothLookup";
import type { LoadState, PresenceRecord } from "../types";

function DrawerCollapseChevrons() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        d="M7 5l6 7-6 7"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13 5l6 7-6 7"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function FloorDrawerPuller({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <button
      type="button"
      className={`floor-drawer-puller${open ? " open" : ""}`}
      aria-expanded={open}
      aria-controls={open ? "floor-drawer-panel" : undefined}
      aria-label="Close floor drawer"
      title="Close drawer"
      onClick={() => onOpenChange(false)}
    >
      <DrawerCollapseChevrons />
    </button>
  );
}

export function FloorDrawer({
  eventKey,
  campaignKey,
  checkInQuery,
  onCheckInQuery,
  onCheckInPick,
  checkInState,
  goingToBooth,
  onGoingToChange,
  onGoingToPick,
  onClearAtAndRoute,
  destinationLabel,
  presence,
  presenceConnected,
  selfUserId,
  open,
  onOpenChange,
  children,
}: {
  eventKey: string;
  campaignKey: string;
  checkInQuery: string;
  onCheckInQuery: (value: string) => void;
  onCheckInPick: (hit: BoothSearchHit) => void;
  checkInState: LoadState<PresenceRecord>;
  goingToBooth: string;
  onGoingToChange: (value: string) => void;
  onGoingToPick: (hit: BoothSearchHit) => void;
  onClearAtAndRoute: () => void;
  destinationLabel: string | null;
  presence: PresenceRecord[];
  presenceConnected: boolean;
  selfUserId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
}) {
  const self = presence.find((entry) => entry.user_id === selfUserId);
  const peekAt = self?.booth_id ? `At ${self.booth_id}` : "I am at";
  const peekTo = self?.destination_booth_id
    ? self.destination_label || self.destination_booth_id
    : destinationLabel
      ? destinationLabel
      : "I am going to";
  const canClearAt = Boolean(checkInQuery.trim() || destinationLabel || self?.booth_id || self?.destination_booth_id);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  return (
    <aside className={`floor-drawer${open ? " open" : ""}`}>
      {open ? (
        <div id="floor-drawer-panel" className="floor-drawer-panel">
            <button
              type="button"
              className="floor-drawer-close"
              aria-label="Close drawer"
              onClick={() => onOpenChange(false)}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                <path
                  d="M2 2l10 10M12 2L2 12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            <div className="floor-drawer-header">
              <span className="floor-drawer-peek-copy">
                {peekAt}
                <span className="muted"> · {peekTo}</span>
              </span>
              <span className={`pill ${presenceConnected ? "ok" : "idle"}`}>
                <span className="dot" />
                {presenceConnected ? "live" : "…"}
              </span>
            </div>
            <div className="floor-drawer-body">
              <div className="floor-drawer-controls">
                <div className="toolbar location-form">
                  <div className="field">
                    <label htmlFor="checkin-booth">I am at</label>
                    <BoothSearchField
                      id="checkin-booth"
                      eventKey={eventKey}
                      campaignKey={campaignKey}
                      value={checkInQuery}
                      onChange={onCheckInQuery}
                      onPick={onCheckInPick}
                    />
                    <button
                      type="button"
                      className="location-clear"
                      onClick={onClearAtAndRoute}
                      disabled={!canClearAt}
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div className="toolbar location-form">
                  <div className="field">
                    <label htmlFor="going-to-booth">I am going to</label>
                    <BoothSearchField
                      id="going-to-booth"
                      eventKey={eventKey}
                      campaignKey={campaignKey}
                      value={goingToBooth}
                      onChange={onGoingToChange}
                      onPick={onGoingToPick}
                    />
                  </div>
                </div>
              </div>
              {checkInState.status === "error" ? <StatusBlock state={checkInState} /> : null}
              {children}
            </div>
          </div>
        ) : null}
    </aside>
  );
}
